/**
 * ★ WA — RODÍZIO de leads entre as SDRs. Puro.
 *
 * Quem entra é quem o gestor MARCOU (`usuarios/{uid}.noRodizio === true`), **não** quem tem a função `sdr`:
 * `funcoesDe` deriva a função do papel quando `funcoes` não existe, e em produção (medido em 22/set/2026)
 * `apenasSdrs(equipe)` devolvia a Jéssica **e o gestor** — metade dos leads cairia no dono sem ninguém pedir.
 * Marca ausente = FORA: estado implícito mente (decisão do dono).
 *
 * A ordem é por `uid` — determinística e estável. Ordenar por nome mudaria a fila quando alguém se casa; a
 * ordem do listener muda a cada snapshot.
 *
 * ★ ESPELHO: `functions/leads/rodizio.js` é cópia deste arquivo (o deploy empacota só `functions/`).
 * `web/src/leads/rodizio.espelho.test.js` passa as mesmas entradas pelos dois e exige saída idêntica.
 */
import {
  estaNoHorario, expedienteDaClinica, expedienteDaPessoa,
} from '@/produtividade/expediente';

/** ⚠ O fuso da clínica que existe. ★ Constante para o espelho não divergir por digitação. */
export const FUSO_PADRAO = 'America/Sao_Paulo';

/** Só ativos marcados. `ativo` ausente conta como ativo (é o padrão do projeto); `noRodizio` ausente, como fora. */
export const noRodizio = (usuario) => usuario?.ativo !== false && usuario?.noRodizio === true;

/** Quem está no rodízio, em ordem estável por uid. */
export const sdrsNoRodizio = (equipe) =>
  (equipe ?? [])
    .filter((u) => noRodizio(u))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));

/**
 * De quem é a vez. Lista vazia → `null`: o lead nasce SEM SDR, nunca um erro que impeça a criação.
 * Ponteiro inválido ou negativo cai em 0 pela dupla módulo — a fila não pode quebrar por dado estranho.
 */
export function proximaSdr(lista, ponteiro) {
  const n = lista?.length ?? 0;
  if (!n) return null;
  const p = Number.isFinite(Number(ponteiro)) ? Math.trunc(Number(ponteiro)) : 0;
  return lista[((p % n) + n) % n];
}

/**
 * O interruptor GERAL da clínica (`config.distribuicaoLeads`). Só `'rodizio'` liga — `'manual'`, ausente e
 * qualquer outro valor desligam. A chave existe desde a criação da clínica (`functions/clinicas.js`) e até a
 * WA ninguém a lia.
 */
export const distribuicaoLigada = (config) => config?.distribuicaoLeads === 'rodizio';

/**
 * ★★★ E7-b (decisão do dono, 09/out/2026) — QUEM ESTÁ NO RODÍZIO **E NO HORÁRIO AGORA**.
 *
 * ⚠⚠ Duas camadas, de propósito, e `sdrsNoRodizio` **não mudou**: ★ ela continua respondendo
 * *"quem o gestor marcou"* — e é o que o Placar e os testes da WA já perguntavam. ★★ Esta aqui
 * responde outra coisa: *"de quem é a vez AGORA"*.
 *
 * ★★★ **E SE NINGUÉM ESTIVER NO HORÁRIO, REPARTE ENTRE TODAS.** ⚠ Decisão do dono, e a razão é
 * o pior estado possível na estreia: ★ um lead de anúncio chegando às 20h **sem dono nenhum**,
 * que ninguém procura no dia seguinte. ⚠⚠ É o problema do `noRodizio` da Damiana de volta, agora
 * por horário. ★★ A pessoa encontra o lead amanhã **com o nome dela** — que é melhor que ninguém.
 *
 * ★ O almoço conta como FORA (`estaNoHorario`): ⚠ dar um lead novo a quem está no intervalo é o
 * mesmo que dá-lo a quem já foi para casa.
 *
 * @param equipe  os documentos de `usuarios` (o `expediente` vem de graça no `get()`)
 * @param agora   instante absoluto; ⚠ `null` **desliga o filtro** (é o comportamento de antes)
 * @param config  `clinicas/{id}.config` — de onde saem o expediente da clínica e o fuso
 */
export function sdrsDisponiveis(equipe, { agora = null, config = null } = {}) {
  const marcados = sdrsNoRodizio(equipe);
  if (!agora || !marcados.length) return marcados;
  const daClinica = expedienteDaClinica(config);
  const fuso = config?.horarioAtendimento?.fusoHorario || FUSO_PADRAO;
  const dentro = marcados.filter((u) => estaNoHorario(expedienteDaPessoa(u, daClinica), agora, fuso));
  // ★★★ A regra do dono, em uma linha: ninguém no horário → o filtro não se aplica.
  return dentro.length ? dentro : marcados;
}
